import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { IGetVideoEntryOptions } from "@app/main/components/videos/models/IGetVideoEntryOptions";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { VideoReviewService } from "@app/main/components/videos/services/video-review.service";
import { VideoEntryUtils } from "@app/main/components/videos/utils/video-entry-utils";
import { CameraDisplayService } from "@components/videos/cameraDisplay.service";
import { VideoStatus } from "@components/videos/enums/VideoStatus";
import { IVideoEntry } from "@components/videos/models/IVideoEntry";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateRange } from "@enums/DateRange.enum";
import { ResourceType } from "@enums/ResourceType";
import { IDriver } from "@interfaces/IDriver";
import { IVehicle } from "@interfaces/IVehicle";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ConstantsService } from "@services/constantsService";
import { IObservationCategory, ObservationsService } from "@services/observations.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import "./video-list.component.scss";

@UntilDestroy()
@Component({
    selector: "video-list",
    template: require("./video-list.component.html")
})
export class VideoListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() vehicle: IVehicle;
    @Input() driver: IDriver;

    // ---

    loading: boolean;
    hideResourceFilter: boolean;

    videos: IVideoEntry[] = [];
    displayVideos: IVideoEntry[] = [];

    accountVehicles: IVehicle[];

    defaultDateRange = DateRange.Latest7Days;
    filterEvent: IAggregateFilterEvent<IVideoEntry>;

    filterTabs: IFilterBarTab<IVideoEntry>[] = [];

    isMobile: boolean;
    observations: IObservationCategory[] = [];


    constructor(
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly observationsService: ObservationsService,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService,
        private readonly veriDate: VeriDate,
        private readonly videoReviewService: VideoReviewService,
        private readonly videoEntryService: VideoEntryService) {
    }

    ngOnInit() {
        this.setupListeners();
        this.createTabs();
        this.createDefaultDateRange();

        this.isMobile = this.constantsService.getIsMobile();

        if (this.isMobile) {
            this.hideResourceFilter = true;
        }

        this.observations = this.observationsService.getObservations();
    }

    ngOnChanges(onChangesObj: angular.IOnChangesObject) {
        if (onChangesObj["vehicle"] || onChangesObj["driver"]) {
            this.getVideos();
        }
    }

    ngOnDestroy() {
        this.vehicle = null;
        this.driver = null;

    }

    playVideo(event: { videoEntry: IVideoEntry, $event: MouseEvent }) {
        this.cameraDisplayService.showVideoPlayer(event.videoEntry, event.$event)
            .then(result => {
                this.getVideos(result.requiresUpdate || result.statusChanged, false);
            });
    }

    onFilterChanged(event: IAggregateFilterEvent<IVideoEntry>) {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (this.filterEvent.trigger == "date") {
            this.getVideos(true);
            return;
        }

        this.filterVideos();
    }

    filterVideoBySearchText(v: IVideoEntry, text: string): boolean {
        if (v.vehicle.ShortReg.toLocaleLowerCase().indexOf(text) >= 0 || v.vehicle.Reg.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        if (v.driver?.Name.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        return false;
    }

    filterVideoByObservation(v: IVideoEntry, observation: IObservationCategory): boolean {
        const foundObservation = v.MetaData?.VideoObservations.find(vc => vc == observation.observation);

        if (isNaN(foundObservation)) {
            return false;
        }

        return true;
    }

    observationSearchPredicate(o: IObservationCategory, searchText: string):boolean {
        return o.translation.toLocaleLowerCase().indexOf(searchText) >= 0;
    }

    // ---

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition('videoList.navLabels.all', (v) => true);
        this.createTabDefinition('videoList.navLabels.new', v => VideoEntryUtils.isUnseen(v));
        this.createTabDefinition('videoList.navLabels.allBookmarks', v => v.MetaData?.Favourite?.length > 0);
    }

    private createTabDefinition(translationString: string, callback: (video: IVideoEntry) => boolean) {

        this.filterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }

    private getVideos(invalidateCache = false, showLoadingIndicator = true): Promise<void> {

        if (!this.filterEvent) {
            return;
        }

        var options: IGetVideoEntryOptions = {
            processEntries: true,
            downloadedOnly: true,
            invalidateCache: invalidateCache
        };

        if (this.vehicle || this.filterEvent.vehicle) {
            options.vehicleFilter = { vehicle: this.vehicle || this.filterEvent.vehicle };
        }

        if (this.driver || this.filterEvent.driver) {
            options.driverFilter = { driver: this.driver || this.filterEvent.driver };
        }

        if (this.driver || this.filterEvent.group) {
            options.groupFilter = { group: this.filterEvent.group, resourceType: ResourceType.Any };
        }

        this.loading = showLoadingIndicator;

        return this.videoEntryService.getVideosEntriesWithMappings(this.filterEvent.date[0], this.filterEvent.date[1], options)
            .then(videos => {
                this.videos = videos.filter(v => v.Status !== VideoStatus.Error && v.Status !== VideoStatus.Cancelled && v.Status !== VideoStatus.Archived && !VideoEntryUtils.hasNoFootage(v));

                this.filterVideos();
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private filterVideos() {
        this.displayVideos = this.filterEvent.filteredData;
        this.createDayBoundaries();
    }

    private createDayBoundaries() {
        // Create day boundaries
        var previousVideo: IVideoEntry;
        this.displayVideos.forEach(video => {
            (video as any).newDay = !previousVideo || !this.veriDate.createMoment(video.VideoStartTime).isSame(previousVideo.VideoStartTime, "d");

            previousVideo = video;
        });
    }

    private createDefaultDateRange() {
        var startEnd = this.veriDate.dateRangeFromEnum(this.defaultDateRange);

        this.filterEvent = { date: [startEnd.start, startEnd.end] };
    }

    private setupListeners() {

        this.videoReviewService.unseenVideoCountUpdated$.pipe(untilDestroyed(this)).subscribe(() => {
            this.getVideos(true, false);
        })

        this.videoReviewService.favouriteVideoCountUpdated$.pipe(untilDestroyed(this)).subscribe(() => {
            this.getVideos(true, false);
        })

        this.videoEntryService.downloadVideoCountUpdated$.pipe(untilDestroyed(this)).subscribe(() => {
            this.getVideos(false, false);
        });
    }
}
